import ReactECharts from "echarts-for-react";
import { PlanDistributionMetric } from "@/services/metrics";
import { formatCurrency } from "@/lib/utils";
import { Loader2 } from "lucide-react";

interface PlanDistributionChartProps {
  data: PlanDistributionMetric[];
  isLoading: boolean;
}

export function PlanDistributionChart({ data, isLoading }: PlanDistributionChartProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[300px]">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[300px] text-muted-foreground text-sm">
        Sin datos para el período seleccionado
      </div>
    );
  }

  // Aggregate by durationDays (latest snapshot per duration)
  const byDuration = data.reduce<Record<number, PlanDistributionMetric>>((acc, item) => {
    if (!acc[item.durationDays] || item.snapshotDate > acc[item.durationDays].snapshotDate) {
      acc[item.durationDays] = item;
    }
    return acc;
  }, {});

  const sorted = Object.values(byDuration).sort((a, b) => a.durationDays - b.durationDays);
  const durations = sorted.map((d) => `${d.durationDays} días`);
  const counts = sorted.map((d) => d.subscriptionCount);
  const revenues = sorted.map((d) => Number(d.revenue));

  const colors = ["#3B82F6", "#8B5CF6", "#06B6D4", "#22C55E", "#F59E0B"];

  const option = {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      backgroundColor: "#fff",
      borderColor: "#e2e8f0",
      borderWidth: 1,
      textStyle: { color: "#1e293b", fontSize: 12 },
      formatter: (params) => {
        const idx = params[0].dataIndex;
        const item = sorted[idx];
        return `<div style="font-weight:600;margin-bottom:4px">${params[0].axisValue}</div>
          <div style="color:#64748b">Suscripciones: <b style="color:#1e293b">${item.subscriptionCount}</b></div>
          <div style="color:#64748b">Ingresos: <b style="color:#1e293b">$${formatCurrency(Number(item.revenue))}</b></div>
          <div style="color:#64748b">Precio promedio: <b style="color:#1e293b">$${formatCurrency(Number(item.avgPrice))}</b></div>
          <div style="color:#64748b">Participación: <b style="color:#1e293b">${item.percentage}%</b></div>`;
      },
    },
    legend: {
      data: ["Suscripciones", "Ingresos"],
      bottom: 0,
      textStyle: { color: "#64748b", fontSize: 11 },
      icon: "roundRect",
      itemWidth: 10,
      itemHeight: 6,
    },
    grid: { left: "3%", right: "8%", bottom: "15%", top: "6%", containLabel: true },
    xAxis: [
      {
        type: "category",
        data: durations,
        axisLine: { lineStyle: { color: "#e2e8f0" } },
        axisLabel: { color: "#94a3b8", fontSize: 11 },
      },
    ],
    yAxis: [
      {
        type: "value",
        name: "Suscripciones",
        axisLine: { show: false },
        splitLine: { lineStyle: { color: "#f1f5f9", type: "dashed" } },
        axisLabel: { color: "#94a3b8", fontSize: 11 },
      },
      {
        type: "value",
        name: "Ingresos",
        axisLine: { show: false },
        splitLine: { show: false },
        axisLabel: {
          color: "#94a3b8",
          fontSize: 10,
          formatter: (v) => `$${formatCurrency(v)}`,
        },
      },
    ],
    series: [
      {
        name: "Suscripciones",
        type: "bar",
        yAxisIndex: 0,
        data: counts.map((v, i) => ({
          value: v,
          itemStyle: {
            color: {
              type: "linear",
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: colors[i % colors.length] },
                { offset: 1, color: colors[i % colors.length] + "80" },
              ],
            },
            borderRadius: [4, 4, 0, 0],
          },
        })),
        barMaxWidth: 50,
      },
      {
        name: "Ingresos",
        type: "line",
        yAxisIndex: 1,
        smooth: true,
        symbol: "circle",
        symbolSize: 7,
        data: revenues,
        lineStyle: { color: "#8B5CF6", width: 2.5 },
        itemStyle: { color: "#8B5CF6" },
      },
    ],
  };

  return <ReactECharts option={option} style={{ height: 300 }} opts={{ renderer: "svg" }} />;
}
